import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Building2, FileText, Shield, Stethoscope } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { SEOHead } from "@/components/SEOHead";
import { breadcrumbSchema } from "@/lib/structuredData";
import { localizePath, normalizeLocale } from "@/lib/localeRoutes";

const legalBreadcrumb = breadcrumbSchema([{ name: "Legal", path: "/legal" }]);

const DOCUMENTS = [
  {
    to: "/terms-of-service",
    icon: FileText,
    titleKeys: ["terms.title", "terms.titleHighlight"],
    descriptionKey: "terms.description",
  },
  {
    to: "/physician-terms",
    icon: Stethoscope,
    titleKeys: ["physicianTerms.title", "physicianTerms.titleHighlight"],
    descriptionKey: "physicianTerms.description",
  },
  {
    to: "/insurance-terms",
    icon: Building2,
    titleKeys: ["insuranceTerms.title", "insuranceTerms.titleHighlight"],
    descriptionKey: "insuranceTerms.description",
  },
  {
    to: "/privacy-policy",
    icon: Shield,
    titleKeys: ["privacy.title", "privacy.titleHighlight"],
    descriptionKey: "privacy.description",
  },
] as const;

const LegalHub = () => {
  const { t, i18n } = useTranslation("legal");
  const locale = normalizeLocale(i18n.language);
  const greekTitle = `${t("hub.title")} ${t("hub.titleHighlight")}`;

  return (
    <>
      <SEOHead
        title={locale === "el" ? greekTitle : "Legal"}
        description={locale === "el" ? t("hub.description") : "The agreements and policies that govern Docio Health: Patient Terms of Service, Physician Agreement and Privacy Policy."}
        jsonLd={[locale === "el" ? breadcrumbSchema([{ name: greekTitle, path: "/legal" }]) : legalBreadcrumb]}
      />
      <div className="min-h-screen bg-muted">
        <section className="brand-section border-b border-border bg-brand-surface">
          <div className="brand-container">
            <h1 className="brand-display mb-6">
              {t("hub.title")} <span className="text-primary">{t("hub.titleHighlight")}</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              {t("hub.description")}
            </p>
          </div>
        </section>

        <section className="brand-section">
          <div className="max-w-3xl mx-auto space-y-6">
            {DOCUMENTS.map(({ to, icon: Icon, titleKeys, descriptionKey }) => (
              <Card key={to} className="rounded-2xl border-border shadow-none">
                <Link to={localizePath(to, i18n.language)} className="block">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h2 className="text-lg font-semibold text-foreground">
                        {t(titleKeys[0])} {t(titleKeys[1])}
                      </h2>
                      <p className="text-sm text-muted-foreground mt-1">{t(descriptionKey)}</p>
                      <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary mt-3">
                        {t("hub.view")}
                        <ArrowRight className="h-4 w-4" />
                      </span>
                    </div>
                  </CardContent>
                </Link>
              </Card>
            ))}
          </div>
        </section>
      </div>
    </>
  );
};

export default LegalHub;
